import React, { useMemo, useState, useCallback, useRef } from 'react'
import { composeRef } from 'rc-util/lib/ref';
import { Editor as MainEditor } from './main/ts';
import LocalModal from './components/LocalModal';
import UploadUrlModal from './components/UploadUrlModal/index';
import { Image } from '@tinper/next-ui'
import { setImageToolbar } from './components/ImageToolbar';
import './index.less';

const EditorComponent = (props: any, ref: any) => {
  const { init = {}, locale: propsLocale, value, initialValue } = props;
  const [showLocalModal, setShowLocalModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [previewSrc, setPreviewSrc] = useState('');
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState({});
  const [videoPosters, setVideoPosters] = useState({});
  const [showUploadUrlModal, setShowUploadUrlModal] = useState(false)
  const editorRef = useRef(null)
  const imageRef = useRef(null);
  const editorMergeRef = composeRef(ref, editorRef);

  const valueProps = {
    value: typeof value === 'string' ? value.replace(/data:img\//gi, 'data:image/') : value,
    initialValue: typeof initialValue === 'string' ? initialValue.replace(/data:img\//gi, 'data:image/') : initialValue
  }

  const handleLocalModal = useCallback((show: boolean) => {
    setShowLocalModal(show);
    if (!show) setModalType('');
  }, [showLocalModal, fileList])

  const renderLocalModal = useCallback((editor: any) => {
    console.log('editor', editor)
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.style.display = 'none';
    input.click();
    input.onchange = async (e: any) => {
      const files = e.target.files;
      if (files && files.length) {
        console.log('files', files);
        const { imageUpload = {} } = props.init;
        if (imageUpload && imageUpload.onUpload) {
          const { onUpload } = imageUpload;
          const res = await onUpload(files)
          // console.log('res', res); // urlList
          setFileList(res);
          setShowLocalModal(true);
        } else {
          defaultOnUpload(files).then((res: any) => {
            // console.log('res', res); // urlList
            setFileList(res);
            setShowLocalModal(true);
          });
        }
      }
    }
  }, [fileList, showLocalModal])

  const locale = useMemo(() => {
    const { language = 'zh-cn' } = init;
    return propsLocale || language.toLocaleLowerCase();
  }, [init, propsLocale])

  const fileListChange = useCallback((fileList: any) => {
    setFileList(fileList);
  }, [fileList])

  const defaultOnUpload = useCallback(async (files: any) => {
    const uploadList = [];
    const readFileAsync = (file: any) => new Promise((resolve) => {
      // eslint-disable-next-line no-undef
      const reader = new FileReader();
      reader.onload = (evt: any) => resolve(evt.target.result);
      reader.readAsDataURL(file);
    });

    for (let i = 0; i < files.length; i++) {
      uploadList.push(await readFileAsync(files[i]));
    }

    return uploadList;
  }, [fileList])

  const renderUrlModal = useCallback((editor: any) => {
    setShowUploadUrlModal(true);
  }, [showUploadUrlModal])

  const proInit = useMemo(() => {
    const { imageUpload } = init;
    return {
      // min_height: 500,
      // language: 'zh_CN',
      // menubar: false, // 顶部菜单栏
      // statusbar: true, // 底部状态栏
      // content_style:
      //     'body { font-size:14px }',
      // default_link_target: '_blank',
      // plugins: 'formatpainter wordlimit preview searchreplace autolink directionality visualblocks visualchars fullscreen image imagetools link media template code codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help emoticons autosave autoresize',
      // toolbar: 'undo redo | formatpainter removeformat | blocks fontfamily fontsize bold italic underline strikethrough charmap | forecolor backcolor | alignleft aligncenter alignright alignjustify lineheight outdent indent | bullist numlist | link unlink | table image media | hr pagebreak | fullscreen',
      ...init,
      imageUpload: {
        maxBanchSize: 33,
        ...imageUpload,
        renderLocalModal,
        renderUrlModal
      }
    }
  }, [init])

  function captureFrame (videoSrc: string, time = 0) {
    return new Promise((resolve) => {
      const videoElement = document.createElement('video');
      videoElement.currentTime = time; // 设置截取的帧时间
      videoElement.muted = true;
      videoElement.autoplay = true;
      videoElement.src = videoSrc;
      videoElement.oncanplay = async function () {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
        ctx?.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          resolve(URL.createObjectURL(blob as Blob));
          videoElement.remove();
          canvas.remove();
        });
      };
    });
  };

  const loadPosters = async (res: any) => {
    setLoading(true);
    const posters: any = {};
    for (let i = 0; i < res.length; i++) {
      const videoSrc = res[i];
      posters[videoSrc] = await captureFrame(videoSrc, 0);
    }
    setVideoPosters(posters);
    setLoading(false);
  };

  React.useEffect(() => {
    const editor = editorRef?.current?.editor;

    editor.addCommand('mceLocalMedia', () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = "video/*";
      input.multiple = true;
      input.style.display = 'none';
      input.click();
      input.onchange = async (e: any) => {
        const files = e.target.files as any[];
        if (files && files.length) {
          const videoFiles = Array.from(files).filter(file => file.type.indexOf('video') !== -1);
          console.log('files', videoFiles);
          const { onMediaUpload } = props.init;
          const res = onMediaUpload ? await onMediaUpload(videoFiles) : videoFiles.map(file => URL.createObjectURL(file));
          setFileList(res);
          setModalType('media');
          setShowLocalModal(true);
          loadPosters(res);
        }
      }
    });

    setImageToolbar(editor, locale);
    editor.on('dblclick', function (e: any) {
      const target = e.target;
      if (target.nodeName === 'IMG') {
        setPreviewSrc(target.src);
        imageRef.current.viewerCase.update();
        imageRef.current.viewerCase.view();
      }
    });
  }, [])
  return (
    <>
      <Image id="image-viewer" title={false} navbar={false} toolbar={false} ref={imageRef}
        url={"src"} style={{ display: 'none' }} zIndex={2500}
      >
        <img src={previewSrc} />
      </Image>
      <MainEditor ref={editorMergeRef} {...props} init={proInit} {...valueProps}/>
      <LocalModal modalType={modalType} editor={editorRef?.current?.editor} showModal={showLocalModal} handleLocalModal={handleLocalModal} fileList={fileList} fileListChange={fileListChange} locale={locale} videoPosters={videoPosters} loading={loading} />
      <UploadUrlModal editor={editorRef?.current?.editor} showModal={showUploadUrlModal} handleModalShow={(show: boolean) => setShowUploadUrlModal(show)} locale={locale} />
    </>
  )
}

const Editor = React.forwardRef(EditorComponent)

export default Editor;